import React from "react";
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Typography,
  Divider,
  Stack,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import WorkIcon from "@mui/icons-material/WorkTwoTone";
import LocationOnIcon from "@mui/icons-material/LocationOnTwoTone";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

const PremiumCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 20px 40px rgba(0,0,0,0.04)",
  overflow: "hidden",
  position: "relative",
  background: "#ffffff",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
  },
}));

const CoverWrapper = styled(Box)(({ theme, src }) => ({
  height: "140px",
  width: "100%",
  background: src
    ? `url(${src}) center/cover no-repeat`
    : `linear-gradient(135deg, #1a252f 0%, #2c3e50 100%)`,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "50%",
    background: "linear-gradient(to top, rgba(0,0,0,0.2), transparent)",
  },
}));

const LargeAvatar = styled(Avatar)(({ theme }) => ({
  width: 110,
  height: 110,
  border: "6px solid #ffffff",
  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
  backgroundColor: "#f4f6f8",
}));

const InfoRow = ({ icon, text, color = "text.secondary" }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1 }}>
    <Box
      sx={{
        display: "flex",
        p: 0.8,
        borderRadius: "10px",
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        color: "primary.main",
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 18 } })}
    </Box>
    <Typography variant="body2" fontWeight="500" color={color} noWrap>
      {text}
    </Typography>
  </Box>
);

export default function AppProfileCard({ User, Profile }) {
  if (!User) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton
          variant="rounded"
          height={300}
          sx={{ borderRadius: "24px" }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 360, mx: "auto", mt: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5, px: 1 }}
      >
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, color: "text.disabled", letterSpacing: 1.2 }}
        >
          Card Preview
        </Typography>
        <Tooltip title="Verified Pioneer">
          <RocketLaunchIcon
            sx={{ fontSize: 18, color: "#ff715b", opacity: 0.8 }}
          />
        </Tooltip>
      </Stack>

      <PremiumCard>
        <CoverWrapper src={Profile.profileCoverPicture} />

        <CardContent sx={{ pt: 0, textAlign: "center", pb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "-55px",
              mb: 2,
              position: "relative",
            }}
          >
            <LargeAvatar src={Profile.profilePicture} alt={User.firstName} />
            <Box
              sx={{
                position: "absolute",
                bottom: 5,
                right: "calc(50% - 55px)",
                bgcolor: "#ffffff",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: "5px",
                width: 32,
                height: 32,
                boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            >
              <Box
                component="img"
                src="/favicon/contactie_favicon.png"
                alt="verified"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>

          <Typography
            variant="h5"
            sx={{ fontWeight: 800, color: "#1a252f", mb: 0.5 }}
          >
            {User.firstName} {User.lastName}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#ff715b",
              fontWeight: 700,
              bgcolor: alpha("#ff715b", 0.1),
              display: "inline-block",
              px: 1.5,
              py: 0.3,
              borderRadius: "20px",
              fontSize: "0.75rem",
              mb: 3,
            }}
          >
            @{Profile.userName}
          </Typography>

          <Divider sx={{ mb: 3, opacity: 0.6 }} />

          <Stack spacing={2} sx={{ textAlign: "left", px: 1 }}>
            <InfoRow
              icon={<WorkIcon />}
              text={Profile.occupation || "Professional Identity"}
              color={Profile.occupation ? "text.primary" : "text.disabled"}
            />

            <InfoRow
              icon={<LocationOnIcon />}
              text={Profile.country || "Tunisia"}
            />

            <Stack direction="row" spacing={1} sx={{ pt: 1, px: 1 }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#4caf50",
                  }}
                />
                <Typography variant="caption" fontWeight="bold">
                  Active
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  sx={{ color: "text.secondary", textTransform: "capitalize" }}
                >
                  {Profile.gender || "Member"}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </CardContent>

        <Box sx={{ height: 6, width: "100%", bgcolor: "#ff715b" }} />
      </PremiumCard>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          mt: 2,
          color: "text.disabled",
          fontStyle: "italic",
        }}
      >
        Changes are reflected in real-time
      </Typography>
    </Box>
  );
}
